<script setup lang="ts">
import { computed } from 'vue';

import type { IArticlesListItem } from '@modules/articles/types';
import type { ITableColumnsOptions } from '@modules/tables/types';
import VButton from '@components/buttons/VButton/VButton.vue';
import VIconButton from '@components/buttons/VIconButton/VIconButton.vue';
import VCard from '@components/card/VCard/VCard.vue';
import {
  ARTICLES_PERMISSIONS,
  TRANSLATION_GROUP_ADMIN_ARTICLES,
  TRANSLATION_GROUP_AFFILIATE_ARTICLES,
} from '@modules/articles/constants';
import { useArticlesDeleteMutation } from '@modules/articles/mutations';
import { useArticlesListQuery } from '@modules/articles/queries';
import {
  ADMIN_ARTICLES_CREATE_ROUTE_NAME,
  ADMIN_ARTICLES_EDIT_ROUTE_NAME,
} from '@modules/articles/submodules/edit/constants';
import {
  ADMIN_ARTICLES_VIEW_ROUTE_NAME,
  AFFILIATE_ARTICLES_VIEW_ROUTE_NAME,
} from '@modules/articles/submodules/view/constants';
import { useConfirmDialogs } from '@modules/core/composables/dialogs';
import { useI18n, useLocale } from '@modules/i18n/composables';
import { useCurrentNamespace } from '@modules/permissions/composables';
import VTable from '@modules/tables/components/VTable/VTable.vue';
import { useTableData } from '@modules/tables/composables';

const { getTranslation } = useI18n();
const { confirmDelete } = useConfirmDialogs();

const { currentLocale } = useLocale();

const {
  tableState,
  isLoading,
  isFetching,
  data,
  updateTableState,
  refreshTableData,
} = useTableData(useArticlesListQuery, currentLocale);

const columnOptions = computed<ITableColumnsOptions>(() => ({
  locale: {
    localization: {
      translationPath: 'creative.list_locales.',
      translationsForValues: {
        all: getTranslation(TRANSLATION_GROUP_ADMIN_ARTICLES, 'ALL_LANGUAGES').value,
      },
    },
  },
  title: {
    defaultMobilePreview: true,
  },
}));

const { mutateAsync: deleteArticle } = useArticlesDeleteMutation();

const { isAdminNamespace } = useCurrentNamespace();
const title = computed(() => (
  isAdminNamespace.value
    ? getTranslation(TRANSLATION_GROUP_ADMIN_ARTICLES, 'HEADER').value
    : getTranslation(TRANSLATION_GROUP_AFFILIATE_ARTICLES, 'HEADER').value
));

function onDelete(article: IArticlesListItem, event: MouseEvent): void {
  confirmDelete({
    event,
    header: getTranslation(TRANSLATION_GROUP_ADMIN_ARTICLES, 'MODAL_TITLE').value,
    message: getTranslation(TRANSLATION_GROUP_ADMIN_ARTICLES, 'MODAL_QUESTION').value,
    acceptLabel: getTranslation(TRANSLATION_GROUP_ADMIN_ARTICLES, 'MODAL_DELETE').value,
    rejectLabel: getTranslation(TRANSLATION_GROUP_ADMIN_ARTICLES, 'MODAL_CANCEL').value,
    accept: () => {
      void deleteArticle(article.id);
    },
  });
}
</script>

<template>
  <VCard v-data-qa-auto-id="'ArticlesListPage'" type="table">
    <VTable
      :data="data"
      :state="tableState"
      :loading="isLoading"
      :is-fetching="isFetching"
      :title-t-path="title"
      :columns-options="columnOptions"
      @state-update="updateTableState"
      @refresh="refreshTableData"
    >
      <template #recordAction="{ listItem }">
        <VIconButton
          icon="pi-eye"
          :to="{
            name: isAdminNamespace ? ADMIN_ARTICLES_VIEW_ROUTE_NAME : AFFILIATE_ARTICLES_VIEW_ROUTE_NAME,
            params: {
              id: listItem.id,
            },
          }"
        />
        <VIconButton
          v-if="$hasPermission(ARTICLES_PERMISSIONS.UPDATE)"
          icon="pi-file-edit"
          :to="{
            name: ADMIN_ARTICLES_EDIT_ROUTE_NAME,
            params: {
              id: listItem.id,
            },
          }"
        />
        <VIconButton
          v-if="$hasPermission(ARTICLES_PERMISSIONS.DESTROY)"
          icon="pi-trash"
          @click="onDelete(listItem, $event)"
        />
      </template>
      <template
        v-if="$hasPermission(ARTICLES_PERMISSIONS.CREATE)"
        #footerButtons
      >
        <VButton
          :to="{
            name: ADMIN_ARTICLES_CREATE_ROUTE_NAME,
          }"
          :label="$getTranslation(TRANSLATION_GROUP_ADMIN_ARTICLES, 'CREATE')"
        />
      </template>
    </VTable>
  </VCard>
</template>
